import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  MonitoringService,
  AuthenticationService,
  BroadcastAppService,
  BroadcastServicelinesService,
  CONFIG_ENV,
} from '@core/service';
import { PlatformService } from '@core/service/platform.service';
import { IEnvironmentVariables } from 'src/environments/IEnvironment';
import { SecurityService, SessionStorageService } from '@mynexus/mynexus-ui-lib';
import { MenuItem } from 'primeng/api';
import { SubSink } from 'subsink';
import { take } from 'rxjs/operators';
import { FeatureFlags } from '@core/enumeration';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject } from '@angular/core';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss'],
    standalone: false
})
export class MainComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  isOpened = true;
  isNotesOpened = false;
  isExternalUser: boolean;
  menuItems: MenuItem[];
  menuIcon = 'pi pi-bars';
  userName: string;

  constructor(
    public authenticationService: AuthenticationService,
    private router: Router,
    private broadcastAppService: BroadcastAppService,
    private aiService: MonitoringService,
    private securityService: SecurityService,
    private platformService: PlatformService,
    private sessionStorageService: SessionStorageService,
    private broadcastServiceLinesService: BroadcastServicelinesService,
    @Inject(CONFIG_ENV) private configEnv: IEnvironmentVariables
  ) {
    this.userName = this.authenticationService.userName;
  }

  async ngOnInit() {
    this.platformService.getDefaultProduct();
    this.isExternalUser = this.authenticationService.isExternalUser;
    this.menuItems = this.getMenuItems();
    this.registerSidebarData();
    this.setLoggerContext();
  }

  // TODO: this is a temporary fix to prevent the side bar from covering the screen
  // when the dev tools are open for Chromium browsers. See #262836
  ngAfterViewInit(): void {
    // Timeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    // setTimeout(() => {
    //   this.isOpened = true;
    // });
  }

  registerSidebarData() {
    this.broadcastAppService.getNoteSidebarStatus().subscribe(isOpen => {
      this.isNotesOpened = isOpen;
    });
  }

  toggleSidebar() {
    this.isOpened = !this.isOpened;

    this.changeMenuIcon();
    this.broadcastServiceLinesService.broadcastMenuBarStatus(this.isOpened);
  }

  changeMenuIcon(): void {
    this.menuIcon = this.isOpened ? 'pi pi-chevron-left' : 'pi pi-chevron-right';
  }

  changeToDefaultIcon(): void {
    this.menuIcon = 'pi pi-bars';
  }

  logout() {
    this.authenticationService.logout();
    // clear all session storage models
    this.sessionStorageService.clear();
  }

  redirectToHomePage() {
    this.router.navigate(['/home']);
    this.broadcastAppService.broadcastNotes(false, undefined, undefined);
  }

  private getMenuItems(): MenuItem[] {
    // This const should probably be directly referencing the global, but it works as-is, so I'm not changing it
    let menuItems = [{
      order: 99,
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => this.logout(),
    }];
    const hasDailyDashboardPermission = this.securityService.hasAccess([
      'HHDailyDashboard.Read',
      'PACMADailyDashboard.Read',
      'PACCOMDailyDashboard.Read',
      'DMEPOSMedicareDailyDashboard.Read',
      'DMEPOSMedicaidDailyDashboard.Read',
    ]);

    // The order property IS IMPORTANT. This is to satisfy order mentioned in bug 216562.
    if (hasDailyDashboardPermission) {
      menuItems.unshift({
        order: 0,
        label: 'Daily Dashboard',
        icon: 'pi pi-th-large',
        command: () => this.router.navigate(['daily-dashboard']),
      });
    }

    // firstValueFrom was not working here, as external users do not have either of these permissions
    // see bug 212132 for more info
    this.authenticationService.getPermissionsByAreas(['ResourceRouter', 'CodesMgmt', 'HHProviderFinder'])
      .pipe(take(1),
      takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: menuPermissions => {
          if (menuPermissions) {
            const resourceRouterPermissions = menuPermissions['ResourceRouter'];
            const codesMgmtPermissions = menuPermissions['CodesMgmt'];
            const hhProviderFinderPermissions = menuPermissions['HHProviderFinder'];

            if (resourceRouterPermissions?.length) {
              menuItems.unshift({
                order: 1,
                label: 'Resource Router',
                icon: 'pi pi-align-justify',
                command: () => window.open(this.configEnv.routerUrl),
              });
            }

            if (codesMgmtPermissions?.length && this.configEnv.codeManagementUrl) {
              menuItems.unshift({
                order: 2,
                label: 'Code Management',
                icon: 'pi pi-bars',
                command: () => window.open(this.configEnv.codeManagementUrl),
              });
            }

            if (hhProviderFinderPermissions?.length && this.configEnv.hhProviderFinderUrl) {
              menuItems.unshift({
                order: 3,
                label: 'HH Provider Finder',
                icon: 'pi pi-search',
                command: () => window.open(this.configEnv.hhProviderFinderUrl),
              });
            }

            this.menuItems = menuItems.sort((a,b) => a.order - b.order);
          }
        }
      });

      menuItems = menuItems.sort((a,b) => a.order - b.order);

    return menuItems;
  }

  private setLoggerContext() {
    const user = this.authenticationService.user;

    if (user) {
      this.aiService.setAuthenticatedUserContext(user.localAccountId);
    } else {
      this.aiService.clearAuthenticatedUserContext();
    }
  }

  protected readonly FeatureFlags = FeatureFlags;
}
